import { InputNumber } from "@/dom/components/Input/Input";
import { Column } from "@/dom/components/Panel/Column";
import { Row } from "@/dom/components/Panel/Row";
import { Section } from "@/dom/components/Panel/Section";
import { useDemoState } from "@/state/demoState";
import { WallKey } from "@/state/RoomConfig/RoomConfig";
import React, { useMemo } from "react";

import trashSvg from "public/assets/svg/trash-one.svg";
import { IconButton } from "@/dom/components/IconButton/IconButton";

import css from "./WallPropsSections.module.css";
import { Button } from "@/dom/components/Button/Button";
import { Typography } from "@/dom/components/Typography/Typography";

import plusSvg from "public/assets/svg/plus.svg";
import {
    WALL_PROP_MIN_HEIGHT,
    WALL_PROP_MIN_WIDTH,
    WallDoorConfig,
    WallPropConfig,
    WallWindowConfig,
} from "@/state/RoomConfig/WallConfig";
import { createWallProp } from "@/state/RoomConfig/createWallProp";
import { Color } from "three";
import { SELECTED_ITEM_TYPE } from "@/3d/SelectedItem";
import { getWallPropAreaData } from "@/3d/roomScheme/WallPropAreaData";
import { LayoutFunctions } from "@/3d/area/LayoutFunctions";
import { round } from "lodash";

type WallPropsSectionsProps = {
    wallKey: WallKey;
};

type PropData = {
    prop: WallPropConfig;
    index: number;
};

type WallPropUpdateData = Partial<Omit<WallPropConfig, "key" | "type">>;

export const WallPropsSections: React.FC<WallPropsSectionsProps> = ({ wallKey }) => {
    const roomConfig = useDemoState((state) => state.roomConfig);
    const setRoomConfig = useDemoState((state) => state.setRoomConfig);

    const selectedItem = useDemoState((state) => state.selectedItem);
    const setSelectedItem = useDemoState((state) => state.setSelectedItem);

    const wall = roomConfig[wallKey];

    const { windows, doors } = useMemo(() => {
        const propData: PropData[] = wall.props.map((prop, index) => ({ prop, index }));

        const windows = propData.filter((prop) => prop.prop.type === "window");
        const doors = propData.filter((prop) => prop.prop.type === "door");

        return { windows, doors };
    }, [wall.props]);

    const restrictPropWidth = (width: number, index: number): number => {
        const prop = wall.props[index];
        if (!prop) return width;

        const propAreaData = getWallPropAreaData(roomConfig, prop.key, wallKey);
        if (!propAreaData) return prop.width;

        const freeSpace = LayoutFunctions["1axis"].getFreeSpaceForCoordinate(
            propAreaData.root,
            propAreaData.otherChildren,
            prop.offset + prop.width / 2,
            propAreaData.propAreaConfig
        );

        if (!freeSpace) return prop.width;

        const maxWidth = freeSpace.end - prop.offset - propAreaData.propAreaConfig.minEndOffset;

        return round(Math.min(maxWidth, Math.max(WALL_PROP_MIN_WIDTH, width)), 3);
    };

    const restrictPropHeight = (height: number, index: number): number => {
        const prop = wall.props[index];
        if (!prop) return height;

        if (height < WALL_PROP_MIN_HEIGHT) return WALL_PROP_MIN_HEIGHT;

        const maxHeight =
            prop.type === "window" ? roomConfig.height - prop.floorOffset : roomConfig.height;
        return round(Math.min(height, maxHeight), 3);
    };

    const restrictPropFloorOffset = (floorOffset: number, index: number): number => {
        const prop = wall.props[index];
        if (!prop || prop.type !== "window") return floorOffset;

        if (floorOffset < 0) return 0;

        const maxFloorOffset = roomConfig.height - prop.height;
        return round(Math.min(floorOffset, maxFloorOffset), 3);
    };

    const handleUpdateProp = (data: WallPropUpdateData, index: number) => {
        const prevProp = wall.props[index];
        if (!prevProp || "type" in data) return;

        if ("width" in data && typeof data.width === "number") {
            data.width = restrictPropWidth(data.width, index);
        }

        if ("height" in data && typeof data.height === "number") {
            data.height = restrictPropHeight(data.height, index);
        }

        if ("floorOffset" in data && typeof data.floorOffset === "number") {
            data.floorOffset = restrictPropFloorOffset(data.floorOffset, index);
        }

        const updatedProp = { ...prevProp, ...data };

        const newProps = [...wall.props];
        newProps[index] = updatedProp;
        setRoomConfig({ ...roomConfig, [wallKey]: { ...wall, props: newProps } });
    };

    const handleAddProp = (prop: WallPropConfig) => {
        const newProps = [...wall.props, prop];
        setRoomConfig({ ...roomConfig, [wallKey]: { ...wall, props: newProps } });
    };

    const handleRemoveProp = (index: number) => {
        if (
            selectedItem?.selectedItemType === SELECTED_ITEM_TYPE.WALL_PROP &&
            wall.props[index].key === selectedItem.key
        ) {
            setSelectedItem(undefined);
        }

        const newProps = [...wall.props];
        newProps.splice(index, 1);
        setRoomConfig({ ...roomConfig, [wallKey]: { ...wall, props: newProps } });
    };

    const handleCreateWindow = () => {
        const newWindow = createWallProp("window", 1, wallKey, roomConfig);
        if (newWindow) {
            handleAddProp(newWindow);
        }
    };

    const handleCreateDoor = () => {
        const newDoor = createWallProp("door", 1, wallKey, roomConfig);
        if (newDoor) {
            handleAddProp(newDoor);
        }
    };

    return (
        <>
            <WallPropsSection
                wallProps={windows}
                onRemove={handleRemoveProp}
                onChange={handleUpdateProp}
                onCreate={handleCreateWindow}
                propName="Окно"
            />
            <WallPropsSection
                wallProps={doors}
                onRemove={handleRemoveProp}
                onChange={handleUpdateProp}
                onCreate={handleCreateDoor}
                propName="Дверь"
            />
        </>
    );
};

type WindowsSectionProps = {
    wallProps: PropData[];
    onRemove: (index: number) => void;
    onChange: (data: Partial<WallPropConfig>, index: number) => void;
    onCreate: () => void;
    propName: string;
};

const WallPropsSection: React.FC<WindowsSectionProps> = ({
    wallProps: wallProps,
    onRemove,
    onChange,
    onCreate,
    propName,
}) => {
    return (
        <Section
            title={
                <Row>
                    <span>{propName}</span>
                    <Button variant="text" onClick={onCreate}>
                        <img src={plusSvg} alt="plus" />
                        <Typography variant="body-medium">
                            Создать {propName.toLocaleLowerCase()}
                        </Typography>
                    </Button>
                </Row>
            }
        >
            {wallProps.map((wallProp) =>
                wallProp.prop.type === "window" ? (
                    <WindowView
                        key={wallProp.prop.key}
                        window={wallProp.prop}
                        onRemove={() => onRemove(wallProp.index)}
                        onChange={(data) => onChange(data, wallProp.index)}
                    />
                ) : (
                    <DoorView
                        key={wallProp.prop.key}
                        door={wallProp.prop}
                        onRemove={() => onRemove(wallProp.index)}
                        onChange={(data) => onChange(data, wallProp.index)}
                    />
                )
            )}
        </Section>
    );
};

type WindowViewProps = {
    window: WallWindowConfig;
    onRemove: () => void;
    onChange: (data: Partial<WallWindowConfig>) => void;
};

const WindowView: React.FC<WindowViewProps> = ({ window, onRemove, onChange }) => {
    return (
        <Column className={css.propColumn}>
            <Row>
                <Row justify="flex-start">
                    <HighlightIndicator color={window.highlightColor} />
                    <span>Окно</span>
                </Row>
                <IconButton iconSrc={trashSvg} onClick={onRemove} />
            </Row>
            <Row>
                <span>Ширина (мм)</span>
                <InputNumber
                    width="small"
                    value={window.width * 1000}
                    onBlur={(value) => onChange({ width: value / 1000 })}
                    step={100}
                />
            </Row>
            <Row>
                <span>Высота (мм)</span>
                <InputNumber
                    width="small"
                    value={window.height * 1000}
                    onBlur={(value) => onChange({ height: value / 1000 })}
                    step={100}
                />
            </Row>
            <Row>
                <span>Высота от пола до окна (мм)</span>
                <InputNumber
                    width="small"
                    value={window.floorOffset * 1000}
                    onBlur={(value) => onChange({ floorOffset: value / 1000 })}
                    step={100}
                />
            </Row>
        </Column>
    );
};

type DoorViewProps = {
    door: WallDoorConfig;
    onRemove: () => void;
    onChange: (data: Partial<WallDoorConfig>) => void;
};

const DoorView: React.FC<DoorViewProps> = ({ door, onRemove, onChange }) => {
    return (
        <Column className={css.propColumn}>
            <Row>
                <Row justify="flex-start">
                    <HighlightIndicator color={door.highlightColor} />
                    <span>Дверь</span>
                </Row>
                <IconButton iconSrc={trashSvg} onClick={onRemove} />
            </Row>
            <Row>
                <span>Ширина (мм)</span>
                <InputNumber
                    width="small"
                    value={door.width * 1000}
                    onBlur={(value) => onChange({ width: value / 1000 })}
                    step={100}
                />
            </Row>
            <Row>
                <span>Высота (мм)</span>
                <InputNumber
                    width="small"
                    value={door.height * 1000}
                    onBlur={(value) => onChange({ height: value / 1000 })}
                    step={100}
                />
            </Row>
        </Column>
    );
};

const HighlightIndicator: React.FC<{ color: Color }> = ({ color }) => {
    return (
        <div
            className={css.highlightIndicator}
            style={{ backgroundColor: "#" + color.getHexString() }}
        />
    );
};
